// API Client for BookForge 2.0 - Universal Items System

let RUNTIME_API_BASE = null;
export async function getApiBase() {
  if (RUNTIME_API_BASE) return RUNTIME_API_BASE;
  try {
    const res = await fetch('/config.json', { cache: 'no-store' });
    if (res.ok) {
      const cfg = await res.json();
      if (cfg && typeof cfg.API_BASE === 'string' && cfg.API_BASE.length > 0) {
        RUNTIME_API_BASE = cfg.API_BASE;
        return RUNTIME_API_BASE;
      }
    }
  } catch {}
  RUNTIME_API_BASE = import.meta.env.VITE_API_URL || '/api/v1';
  return RUNTIME_API_BASE;
}

// Нормализация публичных URL (для изображений и файлов)
export async function resolveAssetUrl(url) {
  if (!url) return url;
  if (/^https?:\/\//i.test(url)) return url;
  const base = await getApiBase();
  if (/^https?:\/\//i.test(base)) {
    try {
      // Абсолютный base: корректно резолвим абсолютный путь (начинающийся с /)
      return new URL(url, base).toString();
    } catch {
      return url;
    }
  }
  // base относительный (dev) — оставляем как есть, чтобы прошёл через прокси Vite
  return url;
}

// Helper function for API calls
async function apiCall(endpoint, options = {}) {
  const token = localStorage.getItem('authToken');
  
  const config = {
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    },
    ...options,
  };

  try {
    const base = await getApiBase();
    const response = await fetch(`${base}${endpoint}`, config);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
    }
    
    // Gracefully handle empty responses (e.g., 204 No Content) or non-JSON bodies
    if (response.status === 204) {
      return null;
    }
    const contentType = response.headers.get('content-type') || '';
    if (!contentType.includes('application/json')) {
      // Try to read text, but do not fail hard if empty
      const text = await response.text().catch(() => '');
      return text || null;
    }
    
    return await response.json();
  } catch (error) {
    console.error(`API call failed for ${endpoint}:`, error);
    throw error;
  }
}

// Minimal auth API used by AuthContext
export const authAPI = {
  isAuthenticated() {
    return !!localStorage.getItem('authToken');
  },
  clear() {
    localStorage.removeItem('authToken');
  },
  async login(email, password) {
    const res = await apiCall('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    if (res?.token) localStorage.setItem('authToken', res.token);
    return res;
  },
  async logout() {
    try {
      await apiCall('/auth/logout', { method: 'POST' });
    } finally {
      localStorage.removeItem('authToken');
    }
  },
  async register(userData) {
    const res = await apiCall('/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
    if (res?.token) localStorage.setItem('authToken', res.token);
    return res;
  },
  async refreshToken() {
    const res = await apiCall('/auth/refresh', { method: 'POST' });
    if (res?.token) localStorage.setItem('authToken', res.token);
    return res;
  },
  async getCurrentUser() {
    const res = await apiCall('/auth/me');
    return res?.user || res;
  },
  async changePassword(currentPassword, newPassword) {
    return await apiCall('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    });
  },
};

// Universal Items API
export const Items = {
  // List all items with optional filters
  list: (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value);
      }
    });
    const query = params.toString() ? `?${params.toString()}` : '';
    return apiCall(`/items${query}`);
  },

  // Get item by ID
  get: (id) => apiCall(`/items/${id}`),

  // Get item by ISBN
  getByIsbn: (isbn) => apiCall(`/items/isbn/${isbn}`),

  // Get item by GTIN
  getByGtin: (gtin) => apiCall(`/items/gtin/${gtin}`),

  // Create new item
  create: (itemData) => apiCall('/items', {
    method: 'POST',
    body: JSON.stringify(itemData),
  }),

  // Update existing item
  update: (id, itemData) => apiCall(`/items/${id}`, {
    method: 'PUT',
    body: JSON.stringify(itemData),
  }),

  // Delete item
  delete: (id) => apiCall(`/items/${id}`, {
    method: 'DELETE',
  }),

  // Get item components
  getComponents: (id) => apiCall(`/items/${id}/components`),

  // Add component to item
  addComponent: (id, componentData) => apiCall(`/items/${id}/components`, {
    method: 'POST',
    body: JSON.stringify(componentData),
  }),

  // Remove component from item
  removeComponent: (componentId) => apiCall(`/items/components/${componentId}`, {
    method: 'DELETE',
  }),

  // Calculate bundle weight
  calculateWeight: (id) => apiCall(`/items/${id}/calculate-weight`, {
    method: 'POST',
  }),
};

// Inventory Movements API
export const Inventory = {
  // List inventory movements
  list: (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value);
      }
    });
    const query = params.toString() ? `?${params.toString()}` : '';
    return apiCall(`/inventory${query}`);
  },

  // Get movement by ID
  get: (id) => apiCall(`/inventory/${id}`),

  // Get movement with item details
  getWithItems: (id) => apiCall(`/inventory/${id}/with-items`),

  // Create new movement
  create: (movementData) => apiCall('/inventory', {
    method: 'POST',
    body: JSON.stringify(movementData),
  }),

  // Update movement
  update: (id, movementData) => apiCall(`/inventory/${id}`, {
    method: 'PUT',
    body: JSON.stringify(movementData),
  }),

  // Delete movement
  delete: (id) => apiCall(`/inventory/${id}`, {
    method: 'DELETE',
  }),

  // Complete movement
  complete: (id) => apiCall(`/inventory/${id}/complete`, {
    method: 'POST',
  }),

  // Cancel movement
  cancel: (id) => apiCall(`/inventory/${id}/cancel`, {
    method: 'POST',
  }),

  // Get stock levels
  getStockLevels: (warehouseId = null) => {
    const query = warehouseId ? `?warehouse_id=${warehouseId}` : '';
    return apiCall(`/inventory/stock/levels${query}`);
  },

  // Get movement history for item
  getMovementHistory: (itemId, warehouseId = null) => {
    const query = warehouseId ? `?warehouse_id=${warehouseId}` : '';
    return apiCall(`/inventory/stock/history/${itemId}${query}`);
  },
};

// Payment Types API
export const PaymentTypes = {
  // List payment types for admin
  listAdmin: () => apiCall('/payments/types/admin'),

  // List payment types with filters
  list: (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value);
      }
    });
    const query = params.toString() ? `?${params.toString()}` : '';
    return apiCall(`/payments/types${query}`);
  },

  // Get payment type by ID
  get: (id) => apiCall(`/payments/types/${id}`),

  // Create new payment type
  create: (paymentTypeData) => apiCall('/payments/types', {
    method: 'POST',
    body: JSON.stringify(paymentTypeData),
  }),

  // Update existing payment type
  update: (id, paymentTypeData) => apiCall(`/payments/types/${id}`, {
    method: 'PUT',
    body: JSON.stringify(paymentTypeData),
  }),

  // Delete payment type
  delete: (id) => apiCall(`/payments/types/${id}`, {
    method: 'DELETE',
  }),

  // Toggle payment type active status
  toggleActive: (id, isActive) => apiCall(`/payments/types/${id}/toggle`, {
    method: 'PUT',
    body: JSON.stringify({ is_active: isActive }),
  }),
};

// Print Runs API
export const PrintRuns = { /* unchanged below */ }
